import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { onboarding } from '../api/org';
import { profileCompletion } from '../lib/profileCompletion';
import { Modal } from './Modal';
import { Button } from './ui';

/**
 * "Finish your profile" nudge. After a fresh registration or login, if the
 * org's onboarding profile is less than 80% complete, a closable modal
 * invites the user to finish it. Dismissing is per-session: the modal comes
 * back on the next login until the profile reaches 80%.
 */
export function ProfileNudge() {
  const { profileNudge, dismissProfileNudge, isIndividual } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(0);
  const [missing, setMissing] = useState<string[]>([]);

  useEffect(() => {
    // Individuals have their own onboarding flow; the org profile nudge
    // does not apply to personal workspaces.
    if (!profileNudge || isIndividual) {
      setOpen(false);
      return;
    }
    // Don't nag while the user is already in the onboarding wizard.
    if (location.pathname.startsWith('/app/onboarding')) return;
    let cancelled = false;
    onboarding
      .get()
      .then(({ profile }) => {
        if (cancelled) return;
        const c = profileCompletion(profile);
        if (!c.complete) {
          setPct(c.pct);
          setMissing(c.missing);
          setOpen(true);
        } else {
          dismissProfileNudge();
        }
      })
      .catch(() => {
        if (!cancelled) dismissProfileNudge();
      });
    return () => {
      cancelled = true;
    };
  }, [profileNudge, location.pathname, dismissProfileNudge, isIndividual]);

  const close = () => {
    setOpen(false);
    dismissProfileNudge();
  };

  const goOnboarding = () => {
    close();
    navigate('/app/onboarding');
  };

  const guideWithVac = () => {
    close();
    window.dispatchEvent(new CustomEvent('vac:open', { detail: { mode: 'onboard' } }));
  };

  const shown = missing.slice(0, 3);
  const rest = missing.length - shown.length;

  return (
    <Modal open={open} onClose={close} title="Finish setting up your profile">
      <div className="nudge-body">
        <div
          className="nudge-progress"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Profile completion"
        >
          <div className="nudge-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="nudge-pct">
          <strong>{pct}% complete.</strong> A complete profile means better-fit
          opportunities and a sharper readiness plan.
        </p>
        {shown.length > 0 && (
          <ul className="nudge-missing">
            {shown.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
        {rest > 0 && <p className="text-sm text-muted">+ {rest} more to fill in.</p>}
        <div className="nudge-actions">
          <Button variant="primary" onClick={guideWithVac}>
            ✦ Guide me with Val
          </Button>
          <Button variant="ghost" onClick={goOnboarding}>
            Fill it in myself →
          </Button>
          <Button variant="ghost" onClick={close}>
            Not now
          </Button>
        </div>
      </div>
    </Modal>
  );
}
